import React from 'react';
import { translate } from 'components/Translation';
import Add from '@material-ui/icons/Add';
import Business from '@material-ui/icons/Business';
import styles from 'assets/jss/material-dashboard-pro-react/views/users/detailsStyle';
import { makeStyles } from '@material-ui/core';
import GridContainer from 'components/Grid/GridContainer';
import GridItem from 'components/Grid/GridItem';
import InfoCard from 'components/Info/InfoCard';
import CanPerform from 'components/CanPerform/CanPerform';
import CustomButton from 'components/CustomButtons/Button';
import moment from 'moment';
import _ from 'lodash';
import Badge from 'components/Badge/Badge';
import CustomAvatar from 'components/CustomAvatar/CustomAvatar';
import { AccountCell, DepartmentsCell } from 'components/Cells/Cells';
import ResendEmail from 'views/Users/Details/UserDetails/subcomponents/ResendEmail';
import { BodyM, Caption, HeadingMBold } from 'components/Typography/Typography';
import { SingleDepartment } from 'utils/api/models/departments/singleDepartment';
import { SingleCorpAccount } from 'utils/api/models/accounts/singleCorpAccount';
import ConditionalRender from 'components/ConditionalRender/ConditionalRender';
import BillingAccountPermissionsElement from './BillingAccountPermissionsElement';

// @ts-ignore
const useStyles = makeStyles(styles);

interface IPermissionDepartments extends SingleCorpAccount {
	departments: types.users.IExtendedSinglePermission[];
}

interface IEnterpriseProps {
	user: types.users.IExtendedCorpUserDetails;
	handleEditUserType: () => void;
	isAdmin: boolean;
	isManager: boolean;
	isEndUser: boolean;
	accounts: SingleCorpAccount[];
	departments: SingleDepartment[];
	handleClickEditUserAccount: () => void;
	handleClickEditPermission: (accountId: string) => void;
	handleClickAddPermission: () => void;
	handleClickAddBillingAccountPermission: () => void;
	accountsWithoutPermissions: SingleCorpAccount[];
	canPerformAction: string;
	resendEmail: types.users.IResendEmail;
	handleDeletePermission: (accountId: string, userId: string) => void;
}

const Enterprise = ({
	user,
	handleEditUserType,
	handleClickEditUserAccount,
	handleClickEditPermission,
	handleClickAddPermission,
	handleClickAddBillingAccountPermission,
	isAdmin,
	isManager,
	isEndUser,
	accounts,
	accountsWithoutPermissions,
	canPerformAction,
	resendEmail,
	handleDeletePermission,
}: IEnterpriseProps) => {
	const classes = useStyles();

	const preparedAccounts = ((isAdmin, isManager, accounts): JSX.Element[] => {
		const isUnlimited = (permission: types.users.IExtendedSinglePermission) =>
			permission.permissionExpiration
				? moment(permission.permissionExpiration).format('DD.MM.YYYY')
				: translate('unlimited');

		if (isAdmin) {
			return user.permissions
				.filter(({ type }) => type === 'A')
				.map((account, index) => (
					<GridItem xs={12} sm={6} key={account.accountId}>
						<InfoCard
							rowsHeader={[translate('organization.name'), translate('access.expiration.date')]}
							elements={[
								{
									title: (
										<AccountCell
											icon={
												<CustomAvatar
													placeholderIcon={<Business />}
													customClasses={classes.inlineIcon}
													accountId={account.accountId}
												/>
											}
											text={account.accountName || ''}
										/>
									),
									value: isUnlimited(account),
									isTitleObject: true,
								},
							]}
							action={canPerformAction}
							buttons={[
								{
									label: translate('edit'),
									onClick: () => handleClickEditPermission(account.accountId),
									id: `edit-user-permissions-${index}`,
								},
								{
									label: translate('user.details.permissions.delete'),
									onClick: () => handleDeletePermission(account.accountId, user.userData.userId),
									id: `delete-user-permissions-${index}`,
								},
							]}
						/>
					</GridItem>
				));
		} else if (isManager) {
			const accountsMap = accounts.reduce((acc, account) => ({ ...acc, [account.accountId]: account }), {});

			const permissionDepartments: IPermissionDepartments[] = Object.values(
				user.permissions
					.filter(({ type }) => type === 'D')
					.reduce(
						(acc, permission) => ({
							...acc,
							[permission.accountId]: {
								...accountsMap[permission.accountId],
								departments: [..._.get(acc, `${permission.accountId}.departments`, []), permission],
							},
						}),
						{}
					)
			);

			return permissionDepartments.map((account, index) => (
				<GridItem xs={12} sm={6} key={account.accountId}>
					<InfoCard
						header={
							<>
								<Caption color="disabled-light">{translate('organization.name')}</Caption>
								<div className={classes.permissionAccountInfo}>
									<AccountCell
										icon={
											<CustomAvatar
												placeholderIcon={<Business />}
												accountId={account.accountId}
												customClasses={classes.inlineIcon}
											/>
										}
										text={account.accountName}
									/>
								</div>
							</>
						}
						action={canPerformAction}
						elements={account.departments.map((department) => ({
							title: (
								<Badge hexColor={department.departmentUiColor} filled>
									{department.departmentName}
								</Badge>
							),
							value: isUnlimited(department),
							isTitleObject: true,
						}))}
						rowsHeader={[translate('departments.department.name'), translate('access.expDate')]}
						buttons={[
							{
								label: translate('edit'),
								onClick: () => handleClickEditPermission(account.accountId),
								id: `edit-user-permissions-${index}`,
							},
						]}
					/>
				</GridItem>
			));
		}
		return [];
	})(isAdmin, isManager, accounts);

	return (
		<>
			<div className={classes.userInformationHeader}>
				<HeadingMBold color="secondary-light" disableMargin>
					{translate('users.details.userInformation')}
				</HeadingMBold>
			</div>
			<GridContainer justify="flex-start" className={classes.userInformationWrapper}>
				<GridItem xs={12} sm={6}>
					<InfoCard
						elements={[
							{
								title: translate('user.type'),
								value: translate(`role.${user.userData.userType}`),
							},
						]}
						buttons={[
							{
								label: translate('edit'),
								onClick: handleEditUserType,
								id: 'edit-user-type-button',
							},
						]}
						noBorder
						action={canPerformAction}
						body={
							<ResendEmail
								lastSentActivationEmailDate={user?.userData?.lastSentActivationEmailDate}
								userType={user?.userData?.userType}
								{...resendEmail}
							/>
						}
					/>
				</GridItem>
				<GridItem xs={12} sm={6}>
					<InfoCard
						elements={[
							{
								title: translate('organization.name'),
								value: (
									<div className={classes.accountIcon}>
										<AccountCell
											icon={
												<CustomAvatar
													placeholderIcon={<Business />}
													accountId={user.userData.accountId}
													customClasses={classes.inlineIcon}
												/>
											}
											text={user.userData.accountName}
										/>
									</div>
								),
								isValueObject: true,
							},
							{
								title: translate('department'),
								value: user.userData.departmentId ? (
									<DepartmentsCell
										departments={
											[
												{
													name: user.userData.departmentName,
													uiColor: user.userData.departmentUiColor,
													deptId: user.userData.departmentId,
												},
											] as SingleDepartment[]
										}
									/>
								) : (
									translate('department.not.assigned')
								),
								isValueObject: !!user.userData.departmentId,
							},
						].filter(Boolean)}
						buttons={[
							{
								label: translate('edit'),
								onClick: handleClickEditUserAccount,
								id: 'edit-user-organization',
							},
						]}
						noBorder
						action={canPerformAction}
					/>
				</GridItem>
			</GridContainer>
			<ConditionalRender
				show={!isEndUser}
				onTrue={() => (
					<div className={classes.permissionsHeader}>
						<HeadingMBold color="secondary-light" inline>
							{translate('permissions')}
						</HeadingMBold>
						<div>
							<CanPerform
								action={canPerformAction}
								yes={
									accountsWithoutPermissions.length !== 0 ? (
										<CustomButton color="tertiary-light" onClick={() => handleClickAddPermission()}>
											<>
												<Add />
												{translate('users.details.addPermissions')}
											</>
										</CustomButton>
									) : null
								}
							/>
						</div>
					</div>
				)}
			/>
			<ConditionalRender
				show={!isEndUser && !preparedAccounts}
				onTrue={() => <BodyM className={classes.noPermissions}>{translate('no.permissions')}</BodyM>}
			/>
			<GridContainer justify="flex-start">
				<ConditionalRender show={!!preparedAccounts} onTrue={() => <>{preparedAccounts}</>} />
				<ConditionalRender
					show={!!user && isManager}
					onTrue={() => (
						<BillingAccountPermissionsElement
							handleClickAddPermission={handleClickAddBillingAccountPermission}
							canPerformAction={canPerformAction}
							user={user}
						/>
					)}
				/>
			</GridContainer>
		</>
	);
};

export default Enterprise;
